<template>
    <b-modal
        id="profile-share-modal"
        modal-class="apprentice-default-modal profile-share-modal"
        body-class="profile-share-modal-body"
        size="xl"
        ok-variant="primary"
        :ok-title="$t('common.close')"
        ok-only
        cancel-variant="outline-primary"
        :cancel-title="$t('common.cancel')"
        :title="$t('profile.share_timeline')"
        content-class="profile-share-modal-content"
        @show="show"
        @ok="handleOk">
        <div class="new-share">
            <div class="description muted-text">
                {{ $t('profile.enter_the_name_and_email_address_of_the_person_you_want_to_share_your_timeline_with') }}
            </div>
            <div class="add-new-share">
                <b-form-group
                    class="name-group"
                    :label="$t('common.name')"
                    label-class="add-share-label-name"
                    label-for="add-share-name">
                    <b-form-input
                        id="add-share-name"
                        v-model="name"
                        trim
                        :placeholder="$t('common.name')"></b-form-input>
                </b-form-group>

                <b-form-group
                    class="email-group"
                    label="E-mail:"
                    label-class="add-share-label-email"
                    label-for="add-share-email">
                    <b-form-input
                        id="add-share-email"
                        v-model="email"
                        trim
                        placeholder="e-mail"></b-form-input>
                </b-form-group>

                <b-form-group
                    class="valid-until-group"
                    :label="$t('profile.available_until')"
                    label-class="add-share-label-valid-until"
                    label-for="add-share-valid-until">
                    <b-form-datepicker
                        class="apprentice-date-picker btn-date-group"
                        id="add-share-valid-until"
                        v-model="validUntil"
                        start-weekday="1"
                        now-button
                        reset-button
                        right
                        locale="nl"
                        today-variant="primary"
                        nav-button-variant="primary"
                        :aria-controls="$t('profile.choose_date')"
                        label-reset-button="reset"
                        :label-no-date-selected="$t('profile.choose_date')"
                        menu-class="datedropdown"
                        :date-format-options="{
                            year: 'numeric',
                            month: 'short',
                            day: '2-digit',
                            weekday: 'short',
                        }"></b-form-datepicker>
                </b-form-group>

                <b-form-group
                    class="email-group"
                    :label="$t('profile.categories')"
                    label-class="add-share-label-email"
                    label-for="add-share-category">
                    <design-2021-apprentice-multiselect-component
                        id="category-select"
                        v-model="selectedCategories"
                        :multiple-select="true"
                        label="title"
                        :options="availableCategories"
                        track-by="id">
                    </design-2021-apprentice-multiselect-component>
                </b-form-group>

                <b-form-group
                    class="year-group"
                    :label="$t('profile.school_years')"
                    label-class="add-share-label-years"
                    label-for="add-share-years">
                    <design-2021-apprentice-multiselect-component
                        v-model="selectedYears"
                        :multiple-select="true"
                        label="season"
                        :options="availableSchoolYears"
                        track-by="id">
                    </design-2021-apprentice-multiselect-component>
                </b-form-group>
            </div>
            <div class="add-share">
                <b-button
                    class="btn-add btn-add-share"
                    variant="primary"
                    @click="addNewShare"
                    :disabled="name === '' || email === '' || validUntil === ''">
                    {{ $t('profile.sent') }}
                </b-button>
            </div>
        </div>

        <div
            class="active-shares"
            v-if="activeShares.length">
            <h3 class="title">{{ $t('profile.shared_timeline') }}</h3>
            <b-alert
                class="col-12 alert-copied"
                :show="dismissTimer"
                dismissible
                variant="info"
                @dismissed="dismissTimer = 0"
                @dismiss-count-down="dismissCountDownChanged">
                {{ $t('profile.the_URL_is_copied') }}
            </b-alert>
            <design-2021-apprentice-data-table
                class="user-profile-shared-timeline-table"
                nosearch
                :fields="fields"
                :items="activeShares">
                <template v-slot:cell(link)="row">
                    <b-input-group class="public-url d-none d-lg-flex">
                        <template v-slot:append>
                            <b-button
                                class="btn-copy btn-copy-to-clipboard remove-icon-margin"
                                variant="primary-icon"
                                :v-b-tooltip="$t('profile.copy_URL')"
                                @click.stop.prevent="copyUrl(row.item.id)">
                            </b-button>
                        </template>
                        <b-form-input
                            readonly
                            aria-readonly="true"
                            :ref="`publicly-url-${row.item.id}`"
                            :id="`publicly-url-${row.item.id}`"
                            :value="row.item.public_url"
                            type="text"></b-form-input>
                    </b-input-group>
                    <b-button
                        class="btn-copy btn-copy-to-clipboard d-block d-lg-none"
                        variant="primary-icon-only"
                        :v-b-tooltip="$t('profile.copy_URL')"
                        @click.stop.prevent="copyUrl(row.item.id)">
                    </b-button>
                </template>

                <template v-slot:cell(expires_at)="row">
                    {{ row.item.expires_at | date_named_month }}
                </template>
            </design-2021-apprentice-data-table>
        </div>
    </b-modal>
</template>

<script>
export default {
    name: 'design-2021-profile-share-modal-component',
    props: {
        categoriesIn: {
            type: Array,
            required: true,
        },
        schoolYears: {
            type: Array,
            required: true,
        },
        seasonsIn: {
            type: Array,
            required: true,
        },
        cardsIn: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            name: '',
            email: '',
            validUntil: '',
            activeShares: [],
            fields: [
                {
                    key: 'name',
                    label: this.$t('common.name'),
                },
                {
                    key: 'email',
                    label: 'E-mail',
                },
                {
                    key: 'expires_at',
                    label: this.$t('profile.expires_on'),
                },
                {
                    key: 'categories',
                    label: this.$t('profile.categories'),
                },
                {
                    key: 'seasons',
                    label: this.$t('profile.school_years'),
                },
                {
                    key: 'link',
                    label: 'Url',
                },
            ],
            urlCopied: false,
            dismissSecs: 5,
            dismissTimer: 0,
            selectedCategories: [],
            selectedYears: [],
            categories: this.categoriesIn,
            cards: this.cardsIn,
        };
    },
    methods: {
        show() {
            this.name = '';
            this.email = '';
            this.validUntil = '';
            this.selectedCategories = [];
            this.selectedYears = [];
        },
        handleOk(bvModalEvent) {
            bvModalEvent.preventDefault();
            if (this.name || this.email || this.validUntil) {
                this.alert
                    .confirm(
                        this.$t('profile.do_not_sent'),
                        this.$t('profile.are_you_sure_you_do_not_want_to_share_the_timeline_you_filled_in'),
                    )
                    .then((confirm) => {
                        if (!confirm) {
                            return;
                        } else {
                            this.close();
                        }
                    });
            } else {
                const self = this;
                Vue.nextTick(function () {
                    self.close();
                });
            }
        },
        close() {
            this.$bvModal.hide('profile-share-modal');
        },
        addNewShare() {
            // Todo: Make Ajax call to backend for adding new share
            // Is this the reason the mail isn't send?
            const data = {
                name: this.name,
                email: this.email,
                date: this.validUntil,
                selectedCategoryIds: this.selectedCategories.map((category) => category.id),
                selectedYearIds: this.selectedYears.map((year) => year.id),
            };

            this.alert.saving();
            this.axios.post(this.routes.profilePublicAccess(), data).then(
                (response) => {
                    this.name = '';
                    this.email = '';
                    this.validUntil = '';

                    this.alert.success();
                    this.getActiveShares();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        copyUrl(id) {
            const name = `publicly-url-${id}`;
            this.$refs[name].id;
            const inputId = this.$refs[name].id;
            let urlCopy = document.getElementById(inputId);
            urlCopy.select();
            const success = document.execCommand('copy');

            if (success) {
                this.dismissTimer = this.dismissSecs;
            }
            window.getSelection().removeAllRanges();
        },

        dismissCountDownChanged(dismissTimer) {
            this.dismissTimer = dismissTimer;
        },

        getActiveShares() {
            // ToDo: Get list of active shares
            this.axios.get(this.routes.profilePublicAccess()).then(
                (response) => {
                    for (const data of response.data) {
                        let categories = data.categories.map((category) => category.title);

                        data.categories = categories.join(', ');

                        let seasons = data.seasons.map((season) => season.season);
                        data.seasons = seasons.join(', ');
                    }

                    this.activeShares = response.data;
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        filterCards() {
            if (this.cardsIn.length >= 1) {
                this.cards = this.cardsIn.map((card) => {
                    if (
                        this.categories.length >= 1 &&
                        this.categories.map((category) => category.id).indexOf(card.category.id) === -1
                    ) {
                        card.category = {
                            color: '#07A1FF',
                            id: 0,
                            title: this.$t('profile.no_category'),
                        };
                        return {
                            ...card,
                        };
                    } else {
                        return card;
                    }
                });

                if (this.selectedYears.length >= 1) {
                    this.cards = this.cards.filter(
                        (card) => this.selectedYears.map((year) => year.season).indexOf(card.school_year) !== -1,
                    );
                }

                if (this.selectedCategories.length >= 1) {
                    this.cards = this.cards.filter(
                        (card) =>
                            this.selectedCategories.map((category) => category.id).indexOf(card.category.id) !== -1,
                    );
                }
            }
        },
    },

    mounted() {
        this.getActiveShares();
        this.cards = this.cardsIn;
    },

    computed: {
        availableCategories() {
            if (this.cardsIn.length >= 1) {
                let categories = [];
                for (const card of this.cardsIn) {
                    if (categories.length >= 1) {
                        if (card.category && card.category.id) {
                            if (!categories.map((category) => category.id).includes(card.category.id)) {
                                categories.push({
                                    id: card.category.id,
                                    title: card.category.title,
                                });
                            }
                        }
                    } else {
                        if (card.category && card.category.id) {
                            categories.push({
                                id: card.category.id,
                                title: card.category.title,
                            });
                        }
                    }
                }

                return categories;
            }

            return [];
        },

        availableSchoolYears() {
            if (this.cardsIn.length >= 1) {
                let years = [];

                for (const card of this.cardsIn) {
                    if (years.length >= 1) {
                        if (!years.includes(card.school_year)) {
                            years.push(card.school_year);
                        }
                    } else {
                        years.push(card.school_year);
                    }
                }

                years = this.seasonsIn.filter((season) => {
                    return years.includes(season.season);
                });

                return years;
            }

            return [];
        },
    },

    watch: {
        selectedCategories: function () {
            this.filterCards();
        },

        selectedYears: function () {
            this.filterCards();
        },
    },
};
</script>

<style scoped lang="scss">
.description,
.add-new-share {
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
}

.new-share {
    padding-bottom: map-get($grid-gutter-spacing, 'twenty-one');
}

.user-profile-shared-timeline-table {
    padding: 0;
}

.public-url {
    .btn {
        padding: map-get($grid-gutter-spacing, 'fifteen') map-get($grid-gutter-spacing, 'ten');
    }
}

.add-share {
    display: flex;
    justify-content: flex-end;
}

@media (min-width: map-get($grid-breakpoints, 'lg')) {
    .add-new-share {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;

        > div {
            flex: 1;

            &:not(:last-child) {
                margin-right: $grid-gutter-width;
            }
        }
    }
}
</style>
